import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { t } from '../../i18n';
import type { FeedItem, ParentOverview } from '../../types';
import { AppShell } from '../shared/AppShell';
import { Button } from '../shared/Button';
import { FeedRow, useFeedPlayer } from './FeedRow';

type ChildFilter = 'all' | number;
type DateFilter = 'today' | 'week' | 'all';

function todayISO(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' });
}

function isoMinusDays(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Tashkent' });
}

export function AudioFeed() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<ParentOverview | null>(null);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [unheardOnly, setUnheardOnly] = useState(false);
  const [childFilter, setChildFilter] = useState<ChildFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const player = useFeedPlayer(items, setItems);

  useEffect(() => {
    api.parentOverview().then(setOverview);
  }, []);

  useEffect(() => {
    setLoading(true);
    setItems([]);
    setNextCursor(null);
    const params: Parameters<typeof api.parentFeed>[0] = { limit: 30 };
    if (unheardOnly) params.unheard_only = true;
    if (childFilter !== 'all') params.child_id = childFilter;
    if (dateFilter === 'today') params.from_date = todayISO();
    else if (dateFilter === 'week') params.from_date = isoMinusDays(6);
    api.parentFeed(params).then((r) => {
      setItems(r.items);
      setNextCursor(r.next_cursor);
      setLoading(false);
    });
  }, [unheardOnly, childFilter, dateFilter]);

  const loadMore = () => {
    if (!nextCursor) return;
    const params: Parameters<typeof api.parentFeed>[0] = { limit: 30, cursor: nextCursor };
    if (unheardOnly) params.unheard_only = true;
    if (childFilter !== 'all') params.child_id = childFilter;
    if (dateFilter === 'today') params.from_date = todayISO();
    else if (dateFilter === 'week') params.from_date = isoMinusDays(6);
    api.parentFeed(params).then((r) => {
      setItems((prev) => [...prev, ...r.items]);
      setNextCursor(r.next_cursor);
    });
  };

  return (
    <AppShell>
      <main className="mx-auto flex min-h-dvh max-w-2xl flex-col gap-4 p-5">
        <header className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} aria-label={t('common.back')} className="text-ink-muted">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[18px] font-medium">{t('parent.feed.title')}</h1>
        </header>

        <div className="flex flex-wrap gap-2">
          <FilterPill active={!unheardOnly && childFilter === 'all'} onClick={() => { setUnheardOnly(false); setChildFilter('all'); }}>
            Hammasi
          </FilterPill>
          <FilterPill active={unheardOnly} onClick={() => { setUnheardOnly(true); }}>
            Eshitilmagan
          </FilterPill>
          {overview?.children.map((c) => (
            <FilterPill key={c.id} active={childFilter === c.id} onClick={() => setChildFilter(c.id)}>
              {c.name}
            </FilterPill>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-surface-muted px-3 py-2">
          <Segmented
            value={dateFilter}
            onChange={setDateFilter}
            options={[
              { value: 'today', label: 'Bugun' },
              { value: 'week', label: 'Hafta' },
              { value: 'all', label: 'Hammasi' },
            ]}
          />
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-[12px] text-ink-muted">
              <input
                type="checkbox"
                checked={player.autoAdvance}
                onChange={(e) => player.setAutoAdvance(e.target.checked)}
              />
              Auto
            </label>
            <Segmented
              value={player.speed}
              onChange={(v) => player.setSpeed(Number(v))}
              options={[
                { value: 1, label: '1×' },
                { value: 1.25, label: '1.25×' },
                { value: 1.5, label: '1.5×' },
              ]}
            />
          </div>
        </div>

        {loading ? (
          <p className="text-center text-[13px] text-ink-muted">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="text-center text-[13px] text-ink-muted">{t('parent.feed.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((item) => (
              <FeedRow
                key={item.key}
                item={item}
                player={player}
                onDeleted={(key) => setItems((prev) => prev.filter((it) => it.key !== key))}
              />
            ))}
          </ul>
        )}

        {nextCursor ? (
          <Button variant="ghost" onClick={loadMore}>
            {t('common.continue')}
          </Button>
        ) : null}
      </main>
    </AppShell>
  );
}

function FilterPill({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={[
        'rounded-full px-3 py-1.5 text-[13px] transition active:scale-[0.97]',
        active ? 'bg-ink text-ink-inverse' : 'bg-surface-muted text-ink-muted',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function Segmented<T extends string | number>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border-hairline border-border bg-surface">
      {options.map((o) => (
        <button
          key={String(o.value)}
          onClick={() => onChange(o.value)}
          className={[
            'px-2.5 py-1 text-[12px] transition',
            value === o.value ? 'bg-ink text-ink-inverse' : 'text-ink-muted',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
